/**
 * Sortable Block Component
 * Wraps a page editor block with @dnd-kit sortable behavior, a contextual
 * floating toolbar (on hover/select), and a right-click context menu.
 */

import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import * as ContextMenu from '@radix-ui/react-context-menu';
import {
  GripVertical,
  Trash2,
  Copy,
  ChevronUp,
  ChevronDown,
  Sparkles,
} from 'lucide-react';
import type { ContentBlock } from '../../types';

interface SortableBlockProps {
  block: ContentBlock;
  isSelected: boolean;
  isHovered: boolean;
  onSelect: () => void;
  onHover: (hovering: boolean) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onEditWithAI?: () => void;
  children: React.ReactNode;
}

const menuItemClass =
  'flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 rounded cursor-pointer outline-none ' +
  'data-[highlighted]:bg-gray-100 data-[highlighted]:text-gray-900';

export function SortableBlock({
  block,
  isSelected,
  isHovered,
  onSelect,
  onHover,
  onDelete,
  onDuplicate,
  onMoveUp,
  onMoveDown,
  onEditWithAI,
  children,
}: SortableBlockProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 50 : undefined,
  };

  return (
    <ContextMenu.Root onOpenChange={(open) => { if (open) onSelect(); }}>
      <ContextMenu.Trigger asChild>
        <div
          ref={setNodeRef}
          style={style}
          {...attributes}
          className={`relative group ${
            isSelected ? 'ring-2 ring-blue-500' : ''
          } ${isHovered && !isSelected ? 'ring-1 ring-blue-300' : ''} ${
            isDragging ? 'shadow-lg' : ''
          }`}
          onClick={onSelect}
          onMouseEnter={() => onHover(true)}
          onMouseLeave={() => onHover(false)}
        >
          {/* Contextual floating toolbar - visible on hover/select */}
          {(isSelected || isHovered) && !isDragging && (
            <div className="absolute -top-10 left-0 flex items-center gap-1 bg-white border rounded-lg shadow-sm px-2 py-1 z-10">
              {/* Drag handle */}
              <button
                className="p-1 hover:bg-gray-100 rounded cursor-grab active:cursor-grabbing"
                title="Drag to reorder"
                {...listeners}
              >
                <GripVertical className="h-4 w-4 text-gray-500" />
              </button>

              <span className="text-xs text-gray-600 px-2 border-l">
                {block.blockName || block.blockType}
              </span>

              <div className="flex items-center gap-1 border-l pl-2">
                <button
                  onClick={(e) => { e.stopPropagation(); onMoveUp(); }}
                  className="p-1 hover:bg-gray-100 rounded"
                  title="Move up"
                >
                  <ChevronUp className="h-4 w-4" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onMoveDown(); }}
                  className="p-1 hover:bg-gray-100 rounded"
                  title="Move down"
                >
                  <ChevronDown className="h-4 w-4" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
                  className="p-1 hover:bg-gray-100 rounded"
                  title="Duplicate"
                >
                  <Copy className="h-4 w-4" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onDelete(); }}
                  className="p-1 hover:bg-red-100 rounded text-red-600"
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {children}
        </div>
      </ContextMenu.Trigger>

      <ContextMenu.Portal>
        <ContextMenu.Content className="min-w-[170px] bg-white border border-gray-200 rounded-lg shadow-lg p-1 z-50">
          {onEditWithAI && (
            <>
              <ContextMenu.Item
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-violet-700 rounded cursor-pointer outline-none data-[highlighted]:bg-violet-50"
                onSelect={() => onEditWithAI()}
              >
                <Sparkles className="h-4 w-4" /> Edit with AI
              </ContextMenu.Item>
              <ContextMenu.Separator className="h-px bg-gray-100 my-1" />
            </>
          )}
          <ContextMenu.Item className={menuItemClass} onSelect={() => onMoveUp()}>
            <ChevronUp className="h-4 w-4" /> Move up
          </ContextMenu.Item>
          <ContextMenu.Item className={menuItemClass} onSelect={() => onMoveDown()}>
            <ChevronDown className="h-4 w-4" /> Move down
          </ContextMenu.Item>
          <ContextMenu.Item className={menuItemClass} onSelect={() => onDuplicate()}>
            <Copy className="h-4 w-4" /> Duplicate
          </ContextMenu.Item>
          <ContextMenu.Separator className="h-px bg-gray-100 my-1" />
          <ContextMenu.Item
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 rounded cursor-pointer outline-none data-[highlighted]:bg-red-50"
            onSelect={() => onDelete()}
          >
            <Trash2 className="h-4 w-4" /> Delete
          </ContextMenu.Item>
        </ContextMenu.Content>
      </ContextMenu.Portal>
    </ContextMenu.Root>
  );
}
